import React, { useState, useRef, useEffect } from "react";
import { FaTimes, FaCalendarAlt, FaUpload } from "react-icons/fa";
import {
  ErrorToast,
  getTimeAddedDate,
  SuccessToast,
  WarningToast,
} from "../../../../Util";
import { CreateMerchantRequests } from "../../../../Actions/RequestActions";
import { Spinner } from "react-bootstrap";

const MerchantRequest = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    date: "",
    narration: "",
    voucherId: "",
    amount: "",
    file: null,
  });

  const [error, setError] = useState(false);
  const [message, setMessage] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "file") {
      setFormData((prevData) => ({
        ...prevData,
        file: files[0] || null,
      }));
    } else if (name === "amount") {
      let cleanValue = value
        .replace(/[^0-9.]/g, "")
        .replace(/(\..*?)\..*/g, "$1")
        .replace(/(\.\d{2})\d+/, "$1");
      setFormData((prevData) => ({
        ...prevData,
        [name]: cleanValue,
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleCreate = () => {
    setFormData({
      date: "",
      narration: "",
      voucherId: "",
      amount: "",
      file: null,
    });
    SuccessToast("Merchant request created successfully.");
    onClose("create");
  };

  const handleSubmit = async (e) => {
    const { date, narration, voucherId, amount, file } = formData;
    const errors = {};
    
    if (!date) errors.date = true;
    if (!narration) errors.narration = true;
    if (!voucherId) errors.voucherId = true;
    if (!amount) errors.amount = true;
    
    setValidationErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      WarningToast("Please fill in all required fields.");
      return;
    }
    
    const formDataToSend = new FormData();
    formDataToSend.append('date', getTimeAddedDate(date));
    formDataToSend.append('narration', narration);
    formDataToSend.append('voucherId', voucherId);
    formDataToSend.append('amount', amount);
    if (file) {
      formDataToSend.append('file', file);
    }
    
    setSubmitting(true);
    CreateMerchantRequests(formDataToSend, (error) => {
      setSubmitting(false);
      setMessage(error);
    }, () => {
      setSubmitting(false);
      handleCreate();
    });
  };

  useEffect(() => {
    if (message && message.type === "error") {
      setSubmitting(false);
      if (message.rawErrors && message.rawErrors.length > 0) {
        message.rawErrors.forEach((error) => {
          const errorMsg = typeof error === 'string' ? error : error.message || error.msg || JSON.stringify(error);
          WarningToast(errorMsg);
        });
      } else if (message.message) {
        WarningToast(message.message);
      }
    }
  }, [message]);

  if (!isOpen) return null;

  return (
    <div className="modal d-block modal-blur-bg">
      <div className="modal-dialog modal-md modal-dialog-centered">
        <div className="modal-content merchant-modal-content">
          <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
            <h2 className="modal-heading-txt mb-0">Merchant Transfer Request</h2>
            <button
              className="btn btn-success"
              onClick={() => onClose("close")}
            >
              Close <FaTimes className="me-1" />
            </button>
          </div>

          <div className="modal-body p-4">
            <div className="row mb-3">
              <div className="col-md-6">
                <label className="form-label">Date <span style={{ color: 'red' }}>*</span></label>
                <div className="position-relative">
                  <input
                    type="date"
                    name="date"
                    id="date-input"
                    className={`form-control date-input-custom ${validationErrors.date ? 'is-invalid' : ''}`}
                    value={formData.date}
                    onChange={handleChange}
                    onClick={(e) => e.target.showPicker()}
                  />
                  <FaCalendarAlt 
                    className="position-absolute end-0 top-50 translate-middle-y me-3 text-muted calendar-icon" 
                    onClick={() => document.getElementById('date-input').showPicker()}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <label className="form-label">Voucher ID <span style={{ color: 'red' }}>*</span></label>
                <input
                  type="text"
                  name="voucherId"
                  className={`form-control ${validationErrors.voucherId ? 'is-invalid' : ''}`}
                  placeholder="ID here"
                  value={formData.voucherId}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">Narration <span style={{ color: 'red' }}>*</span></label>
              <textarea
                name="narration"
                className={`form-control ${validationErrors.narration ? 'is-invalid' : ''}`}
                rows="3"
                placeholder="Comments"
                value={formData.narration}
                onChange={handleChange}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Amount <span style={{ color: 'red' }}>*</span></label>
              <input
                type="text"
                name="amount"
                className={`form-control ${validationErrors.amount ? 'is-invalid' : ''}`}
                placeholder="20,555"
                value={formData.amount}
                onChange={handleChange}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Upload pdf / doc (Optional)</label>
              <div className="file-upload-area">
                <input
                  type="file"
                  name="file"
                  ref={fileInputRef}
                  className="file-input"
                  accept=".pdf,.doc,.docx"
                  onChange={handleChange}
                />
                <label className="file-upload-label" onClick={handleFileClick}>
                  <FaUpload className="upload-icon" />
                  <span>{formData.file ? formData.file.name : 'Upload'}</span>
                </label>
              </div>
            </div>
          </div>

          <div className="modal-footer justify-content-center border-top">
              <button
                className="btn btn-success w-50 d-flex justify-content-center align-items-center"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  "Submit"
                )}
            </button>
          </div>

          {message && (
            <div
              className={`alert mt-3 ${
                message.type === "success" ? "alert-success" : "alert-danger"
              }`}
            >
              {message.text}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MerchantRequest;
